import Box from '@mui/material/Box';
import MuiTooltip from '@mui/material/Tooltip';
import {
  forwardRef,
  ForwardRefExoticComponent,
  PropsWithChildren,
  PropsWithoutRef,
  ReactNode,
  RefAttributes,
} from 'react';

type FR<T, P> = ForwardRefExoticComponent<PropsWithoutRef<P> & RefAttributes<T>>;

export type TooltipPlacement =
  | 'top'
  | 'top-start'
  | 'top-end'
  | 'bottom'
  | 'bottom-start'
  | 'bottom-end'
  | 'left'
  | 'left-start'
  | 'left-end'
  | 'right'
  | 'right-start'
  | 'right-end';

const DEFAULT_PLACEMENT: TooltipPlacement = 'top';
const DEFAULT_ENTER_DELAY = 150;
const DEFAULT_LEAVE_DELAY = 0;
const DEFAULT_MAX_WIDTH = 280;

const TOOLTIP_TYPOGRAPHY_SX = {
  fontSize: 12,
  lineHeight: 1.4,
  fontWeight: 500,
};

const TRIGGER_WRAPPER_SX = {
  display: 'inline-flex',
  outline: 'none',
};

export type Props = {
  content: ReactNode;
  placement?: TooltipPlacement;
  arrow?: boolean;
  enterDelay?: number;
  leaveDelay?: number;
  disabled?: boolean;
  disableInteractive?: boolean;
  followCursor?: boolean;
  maxWidth?: number | string;
  tid?: string;
  triggerTid?: string;
} & PropsWithChildren;

export const Tooltip: FR<HTMLDivElement, Props> = forwardRef((props: Props, ref) => {
  const {
    content,
    placement = DEFAULT_PLACEMENT,
    arrow = true,
    enterDelay = DEFAULT_ENTER_DELAY,
    leaveDelay = DEFAULT_LEAVE_DELAY,
    disabled = false,
    disableInteractive = false,
    followCursor = false,
    maxWidth = DEFAULT_MAX_WIDTH,
    children,
    tid,
    triggerTid,
  } = props;

  const hasContent = !disabled && content !== null && content !== undefined && content !== '';

  if (!hasContent) {
    return (
      <Box ref={ref} component="span" data-testid={triggerTid} sx={TRIGGER_WRAPPER_SX}>
        {children}
      </Box>
    );
  }

  return (
    <MuiTooltip
      // [SECTION] Content
      title={
        <Box component="span" data-testid={tid}>
          {content}
        </Box>
      }
      // [SECTION] Behavior
      placement={placement}
      arrow={arrow}
      enterDelay={enterDelay}
      leaveDelay={leaveDelay}
      disableInteractive={disableInteractive}
      followCursor={followCursor}
      // [SECTION] Style
      slotProps={{
        tooltip: {
          sx: {
            ...TOOLTIP_TYPOGRAPHY_SX,
            maxWidth,
            backgroundColor: '#49454f',
            color: '#ffffff',
            borderRadius: 1,
            px: 1.25,
            py: 0.75,
          },
        },
        arrow: {
          sx: {
            color: '#49454f',
          },
        },
      }}
    >
      <Box ref={ref} component="span" data-testid={triggerTid} sx={TRIGGER_WRAPPER_SX}>
        {children}
      </Box>
    </MuiTooltip>
  );
});
